import { createBrowserRouter, RouterProvider, Navigate } from 'react-router'
import { Layout } from './components/Layout'
import { ToastProvider } from './components/ToastContainer'
import { AuthProvider, useAuth } from './lib/auth'
import { Login } from './pages/Login'
import { AIActivityHistory } from './pages/AIActivityHistory'
import { AIGallery } from './pages/AIGallery'
import { AIReferencePhotos } from './pages/AIReferencePhotos'
import { AIModels } from './pages/AIModels'
import { AIPipeline } from './pages/AIPipeline'
import { AITestGen } from './pages/AITestGen'
import { PromptBlocks } from './pages/PromptBlocks'
import { PromptStats } from './pages/PromptStats'
import { AIQualityRatings } from './pages/AIQualityRatings'
import { Fonts } from './pages/Fonts'
import { SharedImages } from './pages/SharedImages'
import { SharedIcons } from './pages/SharedIcons'
import { SharedMusic } from './pages/SharedMusic'
import { SharedVideos } from './pages/SharedVideos'
import { DbReset } from './pages/DbReset'
import { SyncToProd } from './pages/SyncToProd'
import { Templates } from './pages/Templates'
import { SmartstoreLinks } from './pages/SmartstoreLinks'
import { SmartstoreEvents } from './pages/SmartstoreEvents'
import { SmartstoreProductSearch } from './pages/SmartstoreProductSearch'
import { LoadingDesigns } from './pages/LoadingDesigns'
import { ThankYouDesigns } from './pages/ThankYouDesigns'
import { ThankYouDesignEditor } from './pages/ThankYouDesignEditor'
import { TemplateEditor } from './pages/TemplateEditor'
import { TemplateCompose } from './pages/TemplateCompose'
import { ComposeTemplates } from './pages/ComposeTemplates'
import { StepwiseImport } from './pages/StepwiseImport'
import { ConversionReferences } from './pages/ConversionReferences'
import { ProfanitySettings } from './pages/ProfanitySettings'
import { ReferencePhotoQualitySettings } from './pages/ReferencePhotoQualitySettings'
import { ProductCatalog } from './pages/ProductCatalog'
import { ChatbotFAQs } from './pages/ChatbotFAQs'
import { ChatbotSessions } from './pages/ChatbotSessions'
import { ContactInquiries } from './pages/ContactInquiries'
import { AiQueueMonitor } from './pages/AiQueueMonitor'
import { Invitations } from './pages/Invitations'
import { Users } from './pages/Users'
import { ActivationCodes } from './pages/ActivationCodes'
import { CreditTransactions } from './pages/CreditTransactions'
import { Toss } from './pages/Toss'
import { Payments } from './pages/Payments'
import { RevenueAnalytics } from './pages/RevenueAnalytics'
import { Settlements } from './pages/Settlements'
import { SolapiLog } from './pages/SolapiLog'
import { AuditLogs } from './pages/AuditLogs'
import { Settings } from './pages/Settings'
import { Dashboard } from './pages/Dashboard'
import { Analytics } from './pages/Analytics'
import { PaymentAttribution } from './pages/PaymentAttribution'
import { RevenueReports } from './pages/RevenueReports'
import { MarketingLinks } from './pages/MarketingLinks'
import { InvitationViews } from './pages/InvitationViews'
import { Memos } from './pages/Memos'
import { SectionDesigns } from './pages/SectionDesigns'
import { MenuSettings } from './pages/MenuSettings'
import { MenuVisibilityProvider } from './lib/menu-visibility'
import { TemplateVariables } from './pages/TemplateVariables'

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading, logout } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        로딩 중...
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role !== 'ADMIN') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
        <h2>접근 권한 없음</h2>
        <p>{user?.email} 계정은 관리자가 아닙니다.</p>
        <button onClick={() => logout()} style={{ padding: '8px 24px', borderRadius: '8px', border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: '14px' }}>
          다른 계정으로 로그인
        </button>
      </div>
    )
  }

  return <>{children}</>
}

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/auth-callback', element: <Navigate to="/ops/dashboard" replace /> },
  {
    element: (
      <AdminGuard>
        <MenuVisibilityProvider>
          <Layout />
        </MenuVisibilityProvider>
      </AdminGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/ops/dashboard" replace /> },
      { path: 'ops/dashboard', element: <Dashboard />, handle: { title: '대시보드' } },
      { path: 'ops/users', element: <Users />, handle: { title: '회원' } },
      { path: 'ops/invitations', element: <Invitations />, handle: { title: '청첩장' } },
      { path: 'ops/invitation-views', element: <InvitationViews />, handle: { title: '조회 분석' } },
      { path: 'ops/memos', element: <Memos />, handle: { title: '메모' } },
      { path: 'ai/history', element: <AIActivityHistory />, handle: { title: 'AI 활동 히스토리' } },
      { path: 'ai/gallery', element: <AIGallery />, handle: { title: 'AI 사진 갤러리' } },
      { path: 'ai/reference-photos', element: <AIReferencePhotos />, handle: { title: '유저 참조사진' } },
      { path: 'ai/models', element: <AIModels />, handle: { title: 'AI 모델' } },
      { path: 'ai/pipeline', element: <AIPipeline />, handle: { title: '파이프라인' } },
      { path: 'ai/test', element: <AITestGen />, handle: { title: 'AI 테스트' } },
      { path: 'ai/prompt-blocks', element: <PromptBlocks />, handle: { title: '프롬프트 블록' } },
      { path: 'ai/prompt-stats', element: <PromptStats />, handle: { title: '프롬프트 통계' } },
      { path: 'ai/quality-ratings', element: <AIQualityRatings />, handle: { title: '사진 별점' } },
      { path: 'ai/reference-photo-quality', element: <ReferencePhotoQualitySettings />, handle: { title: '참조사진 품질 임계값' } },
      { path: 'ai/conversion-references', element: <ConversionReferences />, handle: { title: 'Liquid 레퍼런스' } },
      { path: 'ai/chatbot-faqs', element: <ChatbotFAQs />, handle: { title: '챗봇 FAQ' } },
      { path: 'ai/chatbot-sessions', element: <ChatbotSessions />, handle: { title: '챗봇 대화 로그' } },
      { path: 'ai/contact-inquiries', element: <ContactInquiries />, handle: { title: '고객 문의' } },
      { path: 'wedding/templates', element: <Templates />, handle: { title: '템플릿' } },
      { path: 'wedding/templates/import/stepwise', element: <StepwiseImport />, handle: { title: '템플릿 단계별 import' } },
      { path: 'wedding/templates/:id', element: <TemplateEditor />, handle: { title: '템플릿 편집' } },
      { path: 'wedding/templates/:id/compose', element: <TemplateCompose />, handle: { title: '템플릿 조립' } },
      // 조립 진입 전용 목록 — :id 와 겹치지 않도록 templates 하위가 아닌 별도 경로
      { path: 'wedding/compose-templates', element: <ComposeTemplates />, handle: { title: '조립 템플릿' } },
      { path: 'wedding/section-designs', element: <SectionDesigns />, handle: { title: '섹션 라이브러리' } },
      { path: 'wedding/template-variables', element: <TemplateVariables />, handle: { title: '템플릿 변수' } },
      { path: 'wedding/smartstore-links', element: <SmartstoreLinks />, handle: { title: '스마트스토어 링크' } },
      { path: 'wedding/loading-designs', element: <LoadingDesigns />, handle: { title: '로딩 디자인' } },
      { path: 'wedding/thankyou-designs', element: <ThankYouDesigns />, handle: { title: '감사장 디자인' } },
      { path: 'wedding/thankyou-designs/:id', element: <ThankYouDesignEditor />, handle: { title: '감사장 디자인 편집' } },
      { path: 'wedding/fonts', element: <Fonts />, handle: { title: '폰트' } },
      { path: 'wedding/images', element: <SharedImages />, handle: { title: '이미지' } },
      { path: 'wedding/icons', element: <SharedIcons />, handle: { title: '아이콘' } },
      { path: 'wedding/music', element: <SharedMusic />, handle: { title: '음악' } },
      { path: 'wedding/videos', element: <SharedVideos />, handle: { title: '영상' } },
      { path: 'system/products', element: <ProductCatalog />, handle: { title: '상품/가격' } },
      { path: 'commerce/revenue', element: <RevenueAnalytics />, handle: { title: '매출 관리' } },
      { path: 'commerce/settlements', element: <Settlements />, handle: { title: '정산 내역' } },
      { path: 'commerce/payments', element: <Payments />, handle: { title: '결제 내역' } },
      // 광고 ROAS는 '유입·광고 분석'의 탭으로 통합됨 — 기존 링크/북마크 보존용 리다이렉트
      { path: 'commerce/ads', element: <Navigate to="/commerce/attribution?tab=ads" replace /> },
      { path: 'commerce/analytics', element: <Analytics />, handle: { title: 'GA4 분석' } },
      { path: 'commerce/attribution', element: <PaymentAttribution />, handle: { title: '유입·광고 분석' } },
      { path: 'commerce/revenue-reports', element: <RevenueReports />, handle: { title: '매출 리포트' } },
      { path: 'commerce/marketing-links', element: <MarketingLinks />, handle: { title: '마케팅 링크' } },
      { path: 'commerce/activation-codes', element: <ActivationCodes />, handle: { title: '활성화 코드' } },
      { path: 'commerce/credit-transactions', element: <CreditTransactions />, handle: { title: '크레딧 거래' } },
      { path: 'commerce/smartstore-events', element: <SmartstoreEvents />, handle: { title: '스마트스토어 이벤트' } },
      { path: 'commerce/product-search', element: <SmartstoreProductSearch />, handle: { title: '상품 가격 분석' } },
      { path: 'commerce/toss', element: <Toss />, handle: { title: 'Toss 결제' } },
      { path: 'system/settings', element: <Settings />, handle: { title: '설정' } },
      { path: 'system/menus', element: <MenuSettings />, handle: { title: '메뉴 관리' } },
      { path: 'system/profanity', element: <ProfanitySettings />, handle: { title: '욕설 필터' } },
      { path: 'system/solapi-log', element: <SolapiLog />, handle: { title: 'Solapi 발송 로그' } },
      { path: 'system/audit-logs', element: <AuditLogs />, handle: { title: '감사 로그' } },
      { path: 'system/db-reset', element: <DbReset />, handle: { title: 'DB 초기화' } },
      { path: 'system/sync', element: <SyncToProd />, handle: { title: '데이터 동기화' } },
      { path: 'system/ai-queue', element: <AiQueueMonitor />, handle: { title: 'AI 잡 큐 모니터링' } },
    ],
  },
])

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </AuthProvider>
  )
}
